/**
 * AccessDenied — shared 403 surface for route guards.
 *
 * Used by:
 *   - RequirePermissionRoute (variant: 'permission') — user lacks required perm
 *   - RequirePlatformAdminRoute (variant: 'platform') — user is not a platform admin
 *
 * Renders a safe, operator-friendly message. Does NOT expose backend details.
 * Does NOT redirect — the explicit 403 card makes the access boundary visible
 * to both the operator and support teams reviewing screenshots.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'

interface AccessDeniedProps {
  /**
   * 'permission' — the user lacks a required permission for this surface.
   * 'platform'   — the surface is restricted to platform administrators only.
   */
  variant: 'permission' | 'platform'
}

export function AccessDenied({ variant }: AccessDeniedProps) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>
            {variant === 'platform' ? 'Platform administrators only' : 'Access denied'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-fg">
            {variant === 'platform'
              ? 'This page is restricted to platform administrators. If you believe you need access, contact your platform administrator.'
              : "You don't have permission to view this page. If you think this is wrong, contact your team lead."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
